import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, CardHeader, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { createMember } from "../../api/members";
import { NoAccess } from "../../app/NoAccess";
import { Field, FormError, RowsSkeleton, errorMessage, useArea, useIntentKey } from "./common";
import { t } from "./strings";
import s from "./members.module.css";

const A = t.add;
const schema = z.object({
  name: z.string().trim().min(1, A.nameRequired).max(120),
  phone: z.string().trim().min(1, A.phoneRequired),
  email: z.string().trim().max(200).refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), A.emailInvalid),
  homeBranchId: z.string().min(1, A.branchRequired),
});
type Values = z.infer<typeof schema>;

/** Dedupes by phone on the server (D17): a 200 with `created: false` opens the existing member with a toast. */
export function AddMemberPage() {
  const { ctx } = useArea();
  if (ctx.status === "loading") return <RowsSkeleton rows={4} label="Loading" />;
  if (!ctx.has("members.add")) return <NoAccess />;
  return <AddMemberForm />;
}

/** Mounts once the branches are known: the select is uncontrolled, so options arriving later would show one branch and post another. */
function AddMemberForm() {
  const { client, ctx, session } = useArea();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const intent = useIntentKey();
  const branches = ctx.me?.branches ?? [];
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", phone: "", email: "", homeBranchId: session.branchId ?? branches[0]?.id ?? "" },
  });
  const { errors } = form.formState;

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = { name: v.name, phone: v.phone, homeBranchId: v.homeBranchId, ...(v.email ? { email: v.email } : {}) };
      return createMember(client, body, intent.keyFor(body));
    },
    onSuccess: (r) => {
      intent.reset();
      void qc.invalidateQueries({ queryKey: ["members"] });
      toast.show(r.created ? A.created : A.existing);
      navigate(`/members/${r.member.id}`);
    },
  });

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div>
          <Link to="/members" className={s.back}>{t.profile.back}</Link>
          <h1 tabIndex={-1}>{A.title}</h1>
          <p>{A.intro}</p>
        </div>
      </div>
      <Card>
        <CardHeader title={A.title} />
        <form className={`${s.form} ${s.newForm}`} onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
          <Field id="nm-name" label={A.name} error={errors.name?.message}>
            <input id="nm-name" className={`${s.input} ${errors.name ? s.invalid : ""}`} autoComplete="off" maxLength={120} {...form.register("name")} />
          </Field>
          <Field id="nm-phone" label={A.phone} error={errors.phone?.message}>
            <input id="nm-phone" type="tel" inputMode="tel" className={`${s.input} ${errors.phone ? s.invalid : ""}`} autoComplete="off" {...form.register("phone")} />
          </Field>
          <Field id="nm-email" label={A.email} error={errors.email?.message}>
            <input id="nm-email" type="email" className={`${s.input} ${errors.email ? s.invalid : ""}`} autoComplete="off" {...form.register("email")} />
          </Field>
          <Field id="nm-branch" label={A.branch} error={errors.homeBranchId?.message}>
            <select id="nm-branch" className={s.select} {...form.register("homeBranchId")}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <FormError message={save.error ? errorMessage(save.error) : null} />
          <div className={s.headActs}>
            <Button type="submit" variant="primary" busy={save.isPending} busyLabel={A.saving}>{A.save}</Button>
            <Button as={Link} to="/members">{t.drawer.close}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
